import React from "react";
import { Helmet } from "react-helmet-async";
import MainLayout from "../../layouts/MainLayout";

const Career = () => {
  return (
    <MainLayout>
      <Helmet>
        <title>Careers | paralegal.lk</title>
        <meta
          name="description"
          content="Rolling application for internship in Software Engineering and Machine Learning at paralegal.lk"
        />
        <link rel="canonical" href="https://www.paralegal.lk/careers" />
      </Helmet>

      <article className="careers-page w-full max-w-[1080px] mx-auto px-6 md:px-12 py-10 md:py-16 text-pl-text-primary">
        {/* Masthead */}
        <header className="flex flex-wrap justify-between items-baseline gap-y-2 pb-4 mb-10 md:mb-14 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary">
          <div className="flex items-baseline gap-3">
            <span className="text-pl-accent">¶</span>
            <span>careers.paralegal.lk</span>
          </div>
          <span>Call for interns</span>
        </header>

        {/* Hero */}
        <div className="entry e-0 mb-16 md:mb-20">
          <h1 className="font-display leading-[1.05] tracking-[-0.015em] text-[34px] sm:text-[44px] md:text-[58px] lg:text-[64px]">
            Call for <em className="italic text-pl-accent">Internships</em> in
            Software Engineering and Machine Learning at paralegal.lk
          </h1>
        </div>

        {/* § I — Who are we? */}
        <SectionLabel n="I" label="Who are we?" />

        <div className="entry e-1 mb-20 md:mb-24 max-w-[64ch]">
          <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary">
            We are a legal information technology company transforming how
            lawyers and lay people access legal information in Sri Lanka. Our
            first product{" "}
            <a
              href="https://paralegal.lk"
              className="text-pl-accent font-medium hover:underline"
            >
              paralegal.lk
            </a>
            , a fast and powerful legal search engine, is now used by more than
            1,000 lawyers, including leading practitioners and some of the
            largest law firms in the island. Our trilingual legal document translation tool is widely regarded as the most accurate. 
          </p>
        </div>

        {/* § II — Why intern with us? */}
        <SectionLabel n="II" label="Why intern with us?" />

        <div className="entry e-2 mb-20 md:mb-24 max-w-[64ch]">
          <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary mb-5">
            We make sure that all our interns work across our technology stack
            which includes:
          </p>
          <ul className="list-disc list-outside leading-[1.78] mb-5 pl-6 text-15 md:text-16 text-pl-text-secondary space-y-2">
            <li>
              <strong className="text-pl-text-primary font-medium">
                Python and friends:
              </strong>{" "}
              PyTorch, Pandas, NumPy, etc. for machine learning, natural
              language processing, and data science.
            </li>
            <li>
              <strong className="text-pl-text-primary font-medium">
                JavaScript and friends:
              </strong>{" "}
              Node.js, React.js, etc. for web application development.
            </li>
            <li>
              <strong className="text-pl-text-primary font-medium">
                Databases:
              </strong>{" "}
              MongoDB and DynamoDB for database requirements.
            </li>
            <li>
              <strong className="text-pl-text-primary font-medium">
                Cloud services:
              </strong>{" "}
              AWS and GCP for cloud services.
            </li>
            <li>
              <strong className="text-pl-text-primary font-medium">
                Web serving:
              </strong>{" "}
              Nginx for web serving.
            </li>
            <li>
              <strong className="text-pl-text-primary font-medium">
                Payment integration:
              </strong>{" "}
              International payment gateways for transaction processing.
            </li>
          </ul>
          <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary">
            Our interns do not work on toy problems. Within a few weeks, we get
            them contributing production code.
          </p>
        </div>

        {/* § III — Internship Assignment */}
        <SectionLabel n="III" label="Internship Assignment" />

        <div className="entry e-3 mb-20 md:mb-24 max-w-[64ch]">
          <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary mb-6">
            As part of our internship selection process, candidates are required
            to complete a technical assignment. The full assignment details and
            submission requirements are available in the GitHub repository
            below.
          </p>
          <a
            href="https://github.com/ParalegalLK/internship-assignment"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent transition-colors duration-200"
          >
            View Internship Assignment on GitHub
            <span className="transition-transform duration-200 group-hover:translate-x-1">
              →
            </span>
          </a>
        </div>

        {/* § IV — How to Apply */}
        <SectionLabel n="IV" label="How to Apply" />

        <div className="entry e-4 max-w-[64ch] mb-10">
          <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary mb-5">
            If you are interested in joining paralegal.lk, please complete the
            internship assignment and submit your solution as a public GitHub
            repository. Once completed, email the GitHub link along with your
            CV and a brief cover letter to{" "}
            <a
              href="mailto:admin@paralegal.lk"
              className="text-pl-accent font-medium hover:underline"
            >
              admin@paralegal.lk
            </a>
            .
          </p>
          <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary">
            Applications without a completed assignment submission will not be
            considered.
          </p>
        </div>

        <style>{`
          .careers-page .entry { opacity: 0; transform: translateY(12px); animation: cp-in .75s cubic-bezier(.2,.7,.2,1) forwards; }
          .careers-page .e-0 { animation-delay: .04s; }
          .careers-page .e-1 { animation-delay: .10s; }
          .careers-page .e-2 { animation-delay: .14s; }
          .careers-page .e-3 { animation-delay: .10s; }
          .careers-page .e-4 { animation-delay: .10s; }

          @keyframes cp-in { to { opacity: 1; transform: translateY(0); } }

          @media (prefers-reduced-motion: reduce) {
            .careers-page .entry { animation: none; opacity: 1; transform: none; }
          }
        `}</style>
      </article>
    </MainLayout>
  );
};

const SectionLabel = ({ n, label }) => (
  <div className="relative border-t border-pl-border mt-12 mb-10 md:mb-12">
    <div className="absolute -top-[10px] left-0 bg-pl-bg pr-4 font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary flex items-baseline gap-3">
      <span className="text-pl-accent">§ {n}</span>
      <span>{label}</span>
    </div>
  </div>
);

export default Career;
